import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { quotedIlikePattern } from "@/lib/utils/postgrest-filters";
import { Constants } from "@/integrations/supabase/types";
import type { Database } from "@/integrations/supabase/types";

type SaleStatus = Database["public"]["Enums"]["sale_status"];
const isSaleStatus = (value: string): value is SaleStatus => Constants.public.Enums.sale_status.some(status => status === value);

type ItemType = Database["public"]["Enums"]["item_type"];

export interface SaleListItem {
  id: string;
  sale_number: string;
  created_at: string | null;
  total: number | null;
  status: string | null;
  appointment_id: string | null;
  subtotal: number | null;
  discount_amount: number | null;
  buyer_cpf: string | null;
  owner?: { name: string | null; document: string | null } | null;
  pet?: { name: string | null } | null;
  sale_payments?: Array<{
    method: string;
    amount: number;
    received_at: string | null;
  }> | null;
  sale_items?: Array<{
    item?: { type: ItemType | null } | null;
  }> | null;
}

export interface SalesListResult {
  data: SaleListItem[];
  totalCount: number;
  totalPages: number;
  isTruncated?: boolean;
}

export function useSalesList(options?: {
  status?: string;
  statuses?: string[];
  search?: string;
  onlyReceivable?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  /** @deprecated Use page + pageSize for pagination */
  limit?: number;
}) {
  const { data: clinicId } = useCurrentClinicId();
  const { data: customerId } = useCurrentClinicCustomerId();
  const status: SaleStatus = options?.status && isSaleStatus(options.status) ? options.status : "PAGA";
  const statuses = options?.statuses;
  const search = options?.search?.trim() ?? "";
  const onlyReceivable = options?.onlyReceivable ?? false;
  const startDate = options?.startDate;
  const endDate = options?.endDate;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? options?.limit ?? 20;

  return useQuery<SalesListResult>({
    queryKey: ["sales-list", clinicId, customerId, status, statuses, search, onlyReceivable, startDate, endDate, page, pageSize],
    queryFn: async () => {
      if (!clinicId) return { data: [], totalCount: 0, totalPages: 0 };
      const from = (page - 1) * pageSize;
      const to = from + pageSize;

      const filters = Array.isArray(statuses) ? statuses : null;
      const hasDerivedFilters = !!filters;
      const includeReceivable = (filters?.includes("A_RECEBER") ?? false) || onlyReceivable;
      const includePaid = filters?.includes("PAGA") ?? false;
      const includeCancelled = filters?.includes("CANCELADA") ?? false;
      const includeReturned = filters?.includes("DEVOLVIDA") ?? false;
      const usesDerivedPayClassification = hasDerivedFilters && (includeReceivable || includePaid);

      const paymentsSelect = usesDerivedPayClassification
        ? "sale_payments(method, amount, received_at)"
        : includeReceivable
          ? "sale_payments!inner(method, amount, received_at)"
          : "sale_payments(method, amount, received_at)";

      const selectWithPayments = `id, sale_number, created_at, total, status, appointment_id, subtotal, discount_amount, buyer_cpf,
        owner:owners(name, document),
        pet:pets(name),
        sale_items(item:items(type)),
        ${paymentsSelect}` as const;

      const MAX_SEARCH_RESULTS = 2000;
      const MAX_MATCH_IDS = 500;

      const receivableAmount = (row: SaleListItem) => {
        const rows = Array.isArray(row.sale_payments) ? row.sale_payments : [];
        return rows
          .filter((p) => p.method === "A_RECEBER" && !p.received_at)
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      };

      const isDerivedReceivable = (row: SaleListItem) => {
        if (row.status === "CANCELADA" || row.status === "DEVOLVIDA") return false;
        return receivableAmount(row) > 0;
      };

      /**
       * When the UI uses `statuses` as a filter list, treat:
       * - `A_RECEBER` as a derived status (based on payments).
       * - `PAGA` as "fully paid" (status PAGA AND no receivable payments).
       * - `CANCELADA` / `DEVOLVIDA` as raw sale.status.
       *
       * This replaces the old "A receber" toggle.
       */
      const shouldUseDerivedFiltering = usesDerivedPayClassification;
      const startDateTime = startDate ? new Date(`${startDate}T00:00:00`).toISOString() : null;
      const endDateTime = endDate ? new Date(`${endDate}T23:59:59.999`).toISOString() : null;

      // Fast path (no global text search): use DB pagination + exact count.
      if (!search && !shouldUseDerivedFiltering) {
        let query = supabase.from("sales").select(selectWithPayments, { count: "exact" }).eq("clinic_id", clinicId);
        if (Array.isArray(statuses)) {
          const normalizedStatuses: SaleStatus[] = statuses.length > 0 ? statuses.filter(isSaleStatus) : ["PAGA", "CANCELADA", "DEVOLVIDA"];
          query = query.in("status", normalizedStatuses);
        } else {
          query = query.eq("status", status);
        }
        if (onlyReceivable) {
          query = query.eq("sale_payments.method", "A_RECEBER").is("sale_payments.received_at", null);
        }
        if (startDateTime) query = query.gte("created_at", startDateTime);
        if (endDateTime) query = query.lte("created_at", endDateTime);

        const { data, error, count } = await query.order("created_at", { ascending: false }).range(from, to - 1);

        if (error) throw error;
        const totalCount = count ?? 0;
        const totalPages = Math.ceil(totalCount / pageSize) || 1;
        return {
          data: (data ?? []),
          totalCount,
          totalPages,
        };
      }

      const digits = search.replace(/\D/g, "");

      const baseSearchQuery = () => {
        let query = supabase.from("sales").select(selectWithPayments).eq("clinic_id", clinicId);
        if (startDateTime) query = query.gte("created_at", startDateTime);
        if (endDateTime) query = query.lte("created_at", endDateTime);

        if (!shouldUseDerivedFiltering) {
          if (Array.isArray(statuses)) {
            const normalizedStatuses: SaleStatus[] = statuses.length > 0 ? statuses.filter(isSaleStatus) : ["PAGA", "CANCELADA", "DEVOLVIDA"];
            query = query.in("status", normalizedStatuses);
          } else {
            query = query.eq("status", status);
          }
          if (onlyReceivable) {
            query = query.eq("sale_payments.method", "A_RECEBER").is("sale_payments.received_at", null);
          }
          return query.order("created_at", { ascending: false });
        }

        // Derived-filter mode (Status filter includes A_RECEBER / paid-only semantics).
        // We keep DB queries broad and do the final classification client-side.
        return query.order("created_at", { ascending: false });
      };

      const resultsById = new Map<string, SaleListItem>();

      const pushRows = (rows: unknown) => {
        const list = (rows ?? []) as SaleListItem[];
        for (const row of list) {
          if (!row?.id) continue;
          if (!resultsById.has(row.id)) resultsById.set(row.id, row);
        }
      };

      const filterDerivedRows = (rows: SaleListItem[]) => {
        if (!shouldUseDerivedFiltering) return rows;
        return rows.filter((row) => {
          if (includeCancelled && row.status === "CANCELADA") return true;
          if (includeReturned && row.status === "DEVOLVIDA") return true;
          if (includeReceivable && isDerivedReceivable(row)) return true;
          if (includePaid && row.status === "PAGA" && !isDerivedReceivable(row)) return true;
          return false;
        });
      };

      // Derived-filter mode + no search: run a single broad fetch, then classify client-side.
      if (!search && shouldUseDerivedFiltering) {
        const { data, error } = await baseSearchQuery().range(0, MAX_SEARCH_RESULTS - 1);
        if (error) throw error;
        const filtered = filterDerivedRows((data ?? []));
        for (const row of filtered) pushRows([row]);

        const merged = Array.from(resultsById.values()).sort((a, b) => {
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return db - da;
        });

        const isTruncated = merged.length > MAX_SEARCH_RESULTS;
        const finalList = (isTruncated ? merged.slice(0, MAX_SEARCH_RESULTS) : merged) as SaleListItem[];
        const totalCount = finalList.length;
        const totalPages = Math.ceil(totalCount / pageSize) || 1;

        return {
          data: finalList.slice(from, to),
          totalCount,
          totalPages,
          isTruncated: isTruncated || undefined,
        };
      }

      // Query A: number / CPF
      {
        const salePattern = quotedIlikePattern(search);
        const filters = [`sale_number.ilike.${salePattern}`];
        if (digits) filters.push(`buyer_cpf.ilike.${quotedIlikePattern(digits)}`);
        const { data, error } = await baseSearchQuery()
          .or(filters.join(","))
          .range(0, MAX_SEARCH_RESULTS - 1);
        if (error) throw error;
        pushRows(filterDerivedRows((data ?? [])));
      }

      // Query B: resolve owner/pet ids by name via RPC, then fetch sales by owner_id/pet_id
      if (customerId) {
        const { data: matches, error: matchError } = await supabase.rpc("get_owners_with_pets", {
          p_customer_id: customerId,
          p_search: search,
          p_offset: 0,
          p_limit: MAX_MATCH_IDS,
        });

        if (matchError) {
          // If RPC fails (permissions/availability), we still return number/CPF matches.
          // Avoid hard-failing the whole history.
          console.warn("get_owners_with_pets failed for sales search:", matchError);
        } else {
          const rows = matches ?? [];
          const ownerIds = Array.from(
            new Set(rows.map((r) => r.owner_id).filter((v): v is string => !!v)),
          ).slice(0, MAX_MATCH_IDS);
          const petIds = Array.from(
            new Set(rows.map((r) => r.pet_id).filter((v): v is string => !!v)),
          ).slice(0, MAX_MATCH_IDS);

          if (ownerIds.length > 0) {
            const { data, error } = await baseSearchQuery()
              .in("owner_id", ownerIds)
              .range(0, MAX_SEARCH_RESULTS - 1);
            if (error) throw error;
            pushRows(filterDerivedRows((data ?? [])));
          }

          if (petIds.length > 0) {
            const { data, error } = await baseSearchQuery()
              .in("pet_id", petIds)
              .range(0, MAX_SEARCH_RESULTS - 1);
            if (error) throw error;
            pushRows(filterDerivedRows((data ?? [])));
          }
        }
      }

      const merged = Array.from(resultsById.values()).sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });

      const isTruncated = merged.length > MAX_SEARCH_RESULTS;
      const finalList = (isTruncated ? merged.slice(0, MAX_SEARCH_RESULTS) : merged) as SaleListItem[];
      const totalCount = finalList.length;
      const totalPages = Math.ceil(totalCount / pageSize) || 1;

      return {
        data: finalList.slice(from, to),
        totalCount,
        totalPages,
        isTruncated: isTruncated || undefined,
      };
    },
    enabled: !!clinicId,
    placeholderData: keepPreviousData,
  });
}
